// Copyright (c) 2025, California Institute of Technology and contributors
//
// You should have received a copy of the licensing terms for this
// software included in the file "LICENSE" located in the top-level
// directory of this package. If you did not, you can view a copy at
// https://git.ligo.org/ngdd/arrakis-js/-/raw/main/LICENSE

/**
 * GPS epoch start date (January 6, 1980 UTC)
 */
const GPS_EPOCH = new Date("1980-01-06T00:00:00Z").getTime();

/**
 * List of leap seconds added since GPS epoch
 * Source: https://data.iana.org/time-zones/tzdb/leapseconds
 */
const LEAP_SECONDS = [
  "1981-07-01",
  "1982-07-01",
  "1983-07-01",
  "1985-07-01",
  "1988-01-01",
  "1990-01-01",
  "1991-01-01",
  "1992-07-01",
  "1993-07-01",
  "1994-07-01",
  "1996-01-01",
  "1997-07-01",
  "1999-01-01",
  "2006-01-01",
  "2009-01-01",
  "2012-07-01",
  "2015-07-01",
  "2017-01-01",
];

/**
 * Calculates the number of leap seconds that have been added up to a given date
 * @param {Date} date - The date to calculate leap seconds for
 * @returns {number} The number of leap seconds added up to the given date
 */
function calculateLeapSeconds(date: Date): number {
  let totalLeapSeconds = 0;
  for (const leapDate of LEAP_SECONDS) {
    if (date >= new Date(leapDate)) {
      totalLeapSeconds += 1;
    } else {
      break;
    }
  }
  return totalLeapSeconds;
}

/**
 * Converts GPS time to Unix time (seconds since Jan 1, 1970)
 * @param {number} gpsTime - GPS time in miliseconds since Jan 6, 1980 (can be negative for times before the GPS epoch)
 * @returns {number} Unix time in miliseconds
 * @throws {Error} If gpsTime is not a valid number
 */
export function gpsToUnix(gpsTime: number): number {
  if (typeof gpsTime !== "number" || Number.isNaN(gpsTime)) {
    throw new Error("GPS time must be a valid number");
  }

  const gpsDate = new Date(gpsTime + GPS_EPOCH);
  const totalLeapSeconds = calculateLeapSeconds(gpsDate);

  return gpsTime + GPS_EPOCH - totalLeapSeconds * 1000;
}

/**
 * Converts Unix time to GPS time (seconds since Jan 6, 1980)
 * @param {number} unixTime - Unix time in miliseconds since Jan 1, 1970
 * @returns {number} GPS time in miliseconds (can be negative for times before the GPS epoch)
 * @throws {Error} If unixTime is not a valid number
 */
export function unixToGps(unixTime: number): number {
  if (typeof unixTime !== "number" || Number.isNaN(unixTime)) {
    throw new Error("Unix time must be a valid number");
  }

  const unixDate = new Date(unixTime);
  const totalLeapSeconds = calculateLeapSeconds(unixDate);

  return unixTime - GPS_EPOCH + totalLeapSeconds * 1000;
}
